import { useState } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Select,
  Space,
  Popconfirm,
  message,
  Progress,
  Typography,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, AdminRedPacket, RedPacketReconcileRun } from "../api";

const STATUS: Record<number, { color: string; label: string }> = {
  0: { color: "blue", label: "进行中" },
  1: { color: "green", label: "已抢完" },
  2: { color: "orange", label: "已退款" },
};

export default function RedPackets() {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["redpackets", statusFilter],
    queryFn: async () =>
      (
        await api.get<{ items: AdminRedPacket[] }>("/redpackets", {
          params: { status: statusFilter, limit: 100 },
        })
      ).data.items,
  });

  const { data: reconcileRuns } = useQuery({
    queryKey: ["redpacket-reconcile-runs"],
    queryFn: async () =>
      (await api.get<{ items: RedPacketReconcileRun[] }>("/redpackets/reconcile-runs", { params: { limit: 20 } })).data.items,
  });

  const refund = useMutation({
    mutationFn: (id: string) => api.post(`/redpackets/${id}/refund`, {}),
    onSuccess: () => {
      message.success("已标记退款");
      qc.invalidateQueries({ queryKey: ["redpackets"] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "操作失败"),
  });

  return (
    <Card
      style={{ background: "#10131A", border: "1px solid #1A1D26" }}
      title={<span style={{ color: "#F5F5F7" }}>红包管理</span>}
      extra={
        <Select
          allowClear
          placeholder="状态筛选"
          style={{ width: 140 }}
          onChange={(v) => setStatusFilter(v ?? "")}
          options={[
            { value: "0", label: "进行中" },
            { value: "1", label: "已抢完" },
            { value: "2", label: "已退款" },
          ]}
        />
      }
    >
      <Table<AdminRedPacket>
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1200 }}
        columns={[
          {
            title: "合约 ID",
            dataIndex: "contract_id",
            width: 180,
            render: (v: string) => (
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 11 }}>
                {v.slice(0, 10)}…{v.slice(-6)}
              </span>
            ),
          },
          {
            title: "发送方",
            dataIndex: "sender_name",
            render: (v) => v ?? <span style={{ color: "#6B7280" }}>—</span>,
          },
          {
            title: "链",
            dataIndex: "chain",
            width: 90,
            render: (v: string) => <Tag>{v}</Tag>,
          },
          { title: "金额", dataIndex: "total_amount", width: 130 },
          {
            title: "进度",
            width: 160,
            render: (_, r) => (
              <span>
                {r.claimed_count} / {r.total_count}
                <Progress
                  percent={(r.claimed_count / r.total_count) * 100}
                  size="small"
                  showInfo={false}
                />
              </span>
            ),
          },
          {
            title: "类型",
            dataIndex: "is_random",
            width: 90,
            render: (v) => (v ? <Tag color="purple">拼手气</Tag> : <Tag>等额</Tag>),
          },
          {
            title: "状态",
            dataIndex: "status",
            width: 100,
            render: (s: number) => {
              const x = STATUS[s];
              return <Tag color={x?.color}>{x?.label ?? s}</Tag>;
            },
          },
          {
            title: "过期时间",
            dataIndex: "expires_at",
            width: 170,
            render: (v) => new Date(v).toLocaleString(),
          },
          {
            title: "操作",
            width: 200,
            fixed: "right",
            render: (_, r) =>
              r.status !== 2 ? (
                <Space>
                  <Popconfirm
                    title="标记此红包为已退款?"
                    description="仅用于人工回填状态;链上退款必须由红包发送者在 App 内签名发起。"
                    onConfirm={() => refund.mutate(r.id)}
                  >
                    <Button size="small">标记退款</Button>
                  </Popconfirm>
                </Space>
              ) : (
                <span style={{ color: "#6B7280" }}>—</span>
              ),
          },
        ]}
      />

      <Typography.Title level={5} style={{ color: "#F5F5F7", marginTop: 24 }}>
        最近链上对账
      </Typography.Title>
      <Table<RedPacketReconcileRun>
        rowKey="id"
        size="small"
        dataSource={reconcileRuns}
        pagination={false}
        columns={[
          { title: "链", dataIndex: "chain", width: 90, render: (v: string) => <Tag>{v}</Tag> },
          {
            title: "合约",
            dataIndex: "contract",
            width: 160,
            render: (v: string | null) => v ? <span style={{ fontFamily: "JetBrains Mono", fontSize: 11 }}>{v.slice(0, 10)}…{v.slice(-6)}</span> : "-",
          },
          { title: "区块", width: 160, render: (_, r) => `${r.from_block ?? "-"} → ${r.to_block ?? "-"}` },
          { title: "事件", dataIndex: "scanned_events", width: 80 },
          { title: "领取回填", dataIndex: "claims_updated", width: 100 },
          { title: "退款回填", dataIndex: "refunds_updated", width: 100 },
          { title: "失败", dataIndex: "failed_txs", width: 80, render: (v: number) => v ? <Tag color="red">{v}</Tag> : 0 },
          { title: "错误", dataIndex: "error", render: (v: string | null) => v ?? <span style={{ color: "#6B7280" }}>-</span> },
          { title: "时间", dataIndex: "created_at", width: 170, render: (v) => new Date(v).toLocaleString() },
        ]}
      />
    </Card>
  );
}
